import { useEffect, useRef, useState } from "react";

import { FaArrowLeft } from "react-icons/fa";
import "./style.scss";

interface VerifyCodeFormProps {
  email: string;
  onSubmit: (code: string) => Promise<void>;
  onBack: () => void;
  onResend: () => Promise<void>;
  isLoading: boolean;
  errorMsg?: string;
}

export default function VerifyCodeForm({
  email,
  onSubmit,
  onBack,
  onResend,
  isLoading,
  errorMsg,
}: VerifyCodeFormProps) {
  const [code, setCode] = useState<string[]>(Array(6).fill(""));
  const [error, setError] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(
        () => setResendCountdown(resendCountdown - 1),
        1000,
      );
      return () => clearTimeout(timer);
    } else {
      setResendDisabled(false);
    }
  }, [resendCountdown]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setError("");

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();

    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);

    const newCode = [...code];

    for (let i = 0; i < pastedData.length; i++) {
      const digit = pastedData[i];
      if (digit) {
        newCode[i] = digit;
      }
    }

    setCode(newCode);

    const focusIndex = Math.min(pastedData.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const fullCode = code.join("");

    if (fullCode.length !== 6) {
      setError("Por favor, digite o código completo de 6 dígitos");
      return;
    }

    await onSubmit(fullCode);
  };

  const handleResend = async () => {
    setResendDisabled(true);
    setResendCountdown(120);
    await onResend();
  };

  return (
    <form onSubmit={handleSubmit}>
      <button type="button" className="back_button" onClick={onBack}>
        <FaArrowLeft size={16} />
        Voltar
      </button>

      <div className="form_header">
        <h1 className="form_title">Verificar código</h1>
        <p className="form_subtitle">
          Enviamos um código de 6 dígitos para{" "}
          <span className="email_highlight">{email}</span>
        </p>
      </div>

      <div className="code_container" onPaste={handlePaste}>
        {code.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className="code_input"
            disabled={isLoading}
            aria-label={`Dígito ${index + 1} do código`}
          />
        ))}
      </div>

      {(errorMsg || error) && <p className="form_error">{errorMsg || error}</p>}

      <button type="submit" className="submit_button" disabled={isLoading}>
        {isLoading ? (
          <>
            <span className="spinner"></span>
            Verificando...
          </>
        ) : (
          "Confirmar"
        )}
      </button>

      <p className="resend_text">
        Não recebeu o código?{" "}
        <button
          type="button"
          className="resend_link"
          onClick={handleResend}
          disabled={resendDisabled || isLoading}
        >
          {resendDisabled ? `Reenviar em ${resendCountdown}s` : "Reenviar"}
        </button>
      </p>
    </form>
  );
}
